import React, { useCallback, useState } from "react";
import { Link, useLocation, NavLink } from "react-router-dom";
import { CSSTransition } from "react-transition-group";

import { Box, Flex, Grid, Image, Text } from "atoms";
import { SIDEBAR_WIDTH } from "utilities/constants";
import { SidebarNavItem } from "molecules/SidebarNavItem";
import MemoChevronDown from "assets/icons/ChevronDown";
import MemoDashboard from "assets/icons/Dashboard";
import Logo from "assets/img/logo-color.svg";
import MemoPropertyIcon from "assets/icons/PropertyIcon";
import MemoDocumentIcon from "assets/icons/DocumentIcon";
import MemoGearIcon from "assets/icons/GearIcon";

const SIDEBAR_MAP = [
  {
    id: 0,
    title: "Dashboard",
    link: "/dashboard",
    slug: "/dashboard",
    icon: <MemoDashboard height="1.8rem" width="1.8rem" />,
    subItem: [],
  },
  {
    id: 1,
    title: "Property Listing",
    link: "/property-listing",
    slug: "/property-listing",
    icon: <MemoPropertyIcon height="1.8rem" width="1.8rem" />,
    subItem: [],
  },
  {
    id: 2,
    title: "Master",
    link: null,
    slug: "/master",
    icon: <MemoDocumentIcon height="1.8rem" width="1.8rem" />,
    subItem: [
      { id: 0, title: "Location", link: "/master/location" },
      { id: 1, title: "Amenities", link: "/master/amenities" },
      { id: 2, title: "Micromarket", link: "/master/micromarket" },
      { id: 3, title: "Developers", link: "/master/developers" },
      { id: 4, title: "Property Status", link: "/master/property-status" },
      { id: 5, title: "Property Types", link: "/master/property-types" },
      { id: 6, title: "Typology", link: "/master/typology" },
      { id: 7, title: "Nearby Areas", link: "/master/nearby-areas" },
      {
        id: 8,
        title: "Nearby Infrastructures",
        link: "/master/nearby-infrastructures",
      },
      {
        id: 9,
        title: "Nearby Transportation",
        link: "/master/nearby-transporation",
      },
      { id: 10, title: "Specifications", link: "/master/specifications" },
      { id: 10, title: "Infrastructures", link: "/master/infrastructures" },
      { id: 10, title: "Transportations", link: "/master/transporation" },
    ],
  },
  {
    id: 3,
    title: "Settings",
    link: null,
    slug: "/setting",
    icon: <MemoGearIcon height="1.8rem" width="1.8rem" />,
    subItem: [
      {
        id: 0,
        title: "Activity Log",
        link: "/setting/activity-log",
      },
      { id: 1, title: "Roles", link: "/setting/roles" },
      { id: 2, title: "Archive", link: "/setting/archive" },
      { id: 3, title: "Users", link: "/setting/users" },
      { id: 4, title: "Profile", link: "/setting/profile" },
    ],
  },
];

export const Sidebar = () => {
  const [isTabCollapsed, setIsTabCollapsed] = useState(
    new Array(SIDEBAR_MAP.length)
  );

  const location = useLocation();

  const handleTabCollapse = useCallback(
    (index) => {
      let newTabState = JSON.parse(JSON.stringify(isTabCollapsed));
      newTabState[index] = !isTabCollapsed[index];
      setIsTabCollapsed(newTabState);
    },
    [isTabCollapsed]
  );

  return (
    <Box
      zIndex={10}
      overflowY="auto"
      minHeight="100vh"
      maxHeight="100vh"
      transition="all 0.3s"
      width={SIDEBAR_WIDTH}
    >
      {/* logo */}
      <Box
        my="2rem"
        borderBottom="1px solid"
        borderColor="neutral.100"
        mx="2rem"
        pb="1.6rem"
      >
        <Link to="/">
          <Image
            src={Logo}
            objectFit="contain"
            maxWidth="18rem"
            maxHeight="3.7rem"
            mx="auto"
          />
        </Link>
      </Box>

      {/* sidebar maps */}
      {SIDEBAR_MAP.map((item, index) => {
        return (
          <Grid key={index} mb=".8rem">
            {item?.link !== null ? (
              // link without children
              <NavLink to={item?.link || "/"}>
                <Flex
                  aI="center"
                  justifyContent="space-between"
                  mx="1rem"
                  p="1rem"
                  cursor="pointer"
                  borderRadius="1rem"
                  bg={
                    location.pathname === item?.link ? "secondary.50" : "white"
                  }
                >
                  <Flex>
                    <Box
                      color={
                        location.pathname === item?.link
                          ? "secondary.700"
                          : "neutral.800"
                      }
                      height="1.8rem"
                      width="1.8rem"
                    >
                      {item.icon}
                    </Box>
                    <Text
                      truncate
                      px="1rem"
                      fontSize="1.6rem"
                      color={
                        location.pathname === item?.link
                          ? "secondary.700"
                          : "neutral.800"
                      }
                      fontFamily={
                        location.pathname === item?.link
                          ? "NeometricBold"
                          : "NeometricRegular"
                      }
                    >
                      {item?.title}
                    </Text>
                  </Flex>
                </Flex>
              </NavLink>
            ) : (
              // link with children
              <Box>
                <Flex
                  aI="center"
                  justifyContent="space-between"
                  mx="1rem"
                  p="1rem"
                  onClick={() => handleTabCollapse(index)}
                  cursor="pointer"
                  borderRadius="1rem"
                  bg={
                    location.pathname.includes(item?.slug)
                      ? "secondary.50"
                      : "white"
                  }
                >
                  <Flex>
                    <Box
                      color={
                        location.pathname.includes(item?.slug)
                          ? "secondary.700"
                          : "neutral.800"
                      }
                      height="1.8rem"
                      width="1.8rem"
                    >
                      {item.icon}
                    </Box>
                    <Text
                      truncate
                      px="1rem"
                      fontSize="1.6rem"
                      color={
                        location.pathname.includes(item?.slug)
                          ? "secondary.700"
                          : "neutral.800"
                      }
                      fontFamily={
                        location.pathname.includes(item?.slug)
                          ? "NeometricBold"
                          : "NeometricRegular"
                      }
                    >
                      {item?.title}
                    </Text>
                  </Flex>
                  {item?.subItem?.length > 0 && (
                    <Box color="neutral.500">
                      <MemoChevronDown />
                    </Box>
                  )}
                </Flex>
                <CSSTransition
                  classNames="collapse"
                  in={isTabCollapsed[index]}
                  timeout={300}
                  unmountOnExit
                >
                  <Box>
                    {item?.subItem?.map((sublink, index) => {
                      return (
                        <Grid key={index} pl="3.2rem">
                          <NavLink to={sublink?.link || "/dashboard"}>
                            <SidebarNavItem
                              sidebarTitle={sublink?.title}
                              color={
                                location.pathname === sublink?.link
                                  ? "primary.900"
                                  : "neutral.600"
                              }
                              fontFamily={
                                location.pathname === sublink?.link
                                  ? "NeometricBold"
                                  : "NeometricRegular"
                              }
                            />
                          </NavLink>
                        </Grid>
                      );
                    })}
                  </Box>
                </CSSTransition>
              </Box>
            )}
          </Grid>
        );
      })}
    </Box>
  );
};
